export const DEFAULT_PATH_TARGET_FRAC = 0.006

function toLots(rows, side, targetPctFrac) {
  return (rows || [])
    .map((row) => {
      const entry = Number(row?.price || 0)
      const qty = Number(row?.qty || 0)
      return {
        side,
        entry,
        qty,
        target: side === 'long'
          ? entry * (1 + targetPctFrac)
          : entry * (1 - targetPctFrac),
        closable: row?.closable !== false,
        open: true,
      }
    })
    .filter((lot) => lot.entry > 0 && lot.qty > 0)
}

function markOpen(lots, price) {
  let pnl = 0
  let longQty = 0
  let shortQty = 0

  lots.forEach((lot) => {
    if (!lot.open) return
    if (lot.side === 'long') {
      pnl += lot.qty * (price - lot.entry)
      longQty += lot.qty
    } else {
      pnl += lot.qty * (lot.entry - price)
      shortQty += lot.qty
    }
  })

  return { pnl, longQty, shortQty }
}

function targetOnPath(target, fromPrice, toPrice) {
  if (!(target > 0) || !(fromPrice > 0) || !(toPrice > 0)) return false
  if (toPrice > fromPrice) return target > fromPrice && target <= toPrice
  if (toPrice < fromPrice) return target < fromPrice && target >= toPrice
  return false
}

// Sobe: fecha longs cujo target é tocado, do mais próximo ao mais alto.
// Desce: fecha shorts cujo target é tocado, do mais próximo ao mais baixo.
// Depois de cada fechamento a unit sai do livro; o restante é marcado no preço final.
function walkPath(lots, fromPrice, toPrice, closingSide) {
  const open = lots.map((lot) => ({ ...lot, open: true }))
  const hits = open
    .filter((lot) => lot.closable && lot.side === closingSide && targetOnPath(lot.target, fromPrice, toPrice))
    .sort((a, b) => (toPrice > fromPrice ? a.target - b.target : b.target - a.target))

  let realized = 0
  let closedQty = 0

  hits.forEach((lot) => {
    realized += closingSide === 'long'
      ? lot.qty * (lot.target - lot.entry)
      : lot.qty * (lot.entry - lot.target)
    closedQty += lot.qty
    lot.open = false
  })

  const end = markOpen(open, toPrice)
  return {
    endingPnl: realized + end.pnl,
    realized,
    closedQty,
    endLongQty: end.longQty,
    endShortQty: end.shortQty,
    endDelta: end.longQty - end.shortQty,
  }
}

function emptyPath(targetPctFrac) {
  return {
    currentPnl: 0,
    currentPrice: 0,
    targetPrice: 0,
    direction: 'flat',
    targetPctFrac,
    pathPnl: 0,
    pnlChange: 0,
    realizedPnl: 0,
    closedQty: 0,
    endDelta: 0,
    endLongQty: 0,
    endShortQty: 0,
  }
}

export function computePathPnl({
  longs = [],
  shorts = [],
  currentPrice = 0,
  targetPrice = 0,
  targetPctFrac = DEFAULT_PATH_TARGET_FRAC,
} = {}) {
  const price = Number(currentPrice) || 0
  const target = Number(targetPrice) > 0 ? Number(targetPrice) : price
  const direction = target > price ? 'bull' : target < price ? 'bear' : 'flat'
  const targetFrac = Number(targetPctFrac) > 0 ? Number(targetPctFrac) : DEFAULT_PATH_TARGET_FRAC
  if (!(price > 0)) return emptyPath(targetFrac)

  const lots = [
    ...toLots(longs, 'long', targetFrac),
    ...toLots(shorts, 'short', targetFrac),
  ]
  const currentPnl = markOpen(lots, price).pnl
  const walked = direction === 'bull'
    ? walkPath(lots, price, target, 'long')
    : direction === 'bear'
      ? walkPath(lots, price, target, 'short')
      : (() => {
          const end = markOpen(lots, target)
          return {
            endingPnl: end.pnl,
            realized: 0,
            closedQty: 0,
            endLongQty: end.longQty,
            endShortQty: end.shortQty,
            endDelta: end.longQty - end.shortQty,
          }
        })()

  return {
    currentPnl,
    currentPrice: price,
    targetPrice: target,
    direction,
    targetPctFrac: targetFrac,
    pathPnl: walked.endingPnl,
    pnlChange: walked.endingPnl - currentPnl,
    realizedPnl: walked.realized,
    closedQty: walked.closedQty,
    endDelta: walked.endDelta,
    endLongQty: walked.endLongQty,
    endShortQty: walked.endShortQty,
  }
}
